// M24 — Statistics (ARCH §18). PURE (ENG-1): the counters behind Minecraft's
// pause-menu Statistics screen. Two shapes, exactly as Java: a set of general
// "custom" totals (time played, mob kills, deaths…) and per-id category tallies
// (Mined / Used / Picked Up / Killed …, each keyed by a block/item/entity id). The
// host bumps these off the SAME game event flow that drives advancements (mine,
// place, kill, pickup, death, tick); the screen reads them back and formats them.
//
// Representative slice: the registry declares every category Java tracks, but the
// host wires the four with clean existing event sites (mined/used/picked_up/killed)
// plus three custom totals (play_time/mob_kills/deaths). The rest read 0 until a
// later slice wires their sources — the screen renders them all faithfully.

/** Per-id tally categories — each keyed by a block/item/entity id. */
export type StatCategory = 'mined' | 'used' | 'crafted' | 'picked_up' | 'dropped' | 'killed' | 'broken';

/** General single-value totals (Java's "custom" stat group). */
export type CustomStat =
  | 'play_time' // in ticks (20/s)
  | 'mob_kills'
  | 'deaths'
  | 'jumps'
  | 'distance_walked' // in cm
  | 'damage_dealt' // in tenths of a heart
  | 'damage_taken'; // in tenths of a heart

// ── Value formatters (Java formats time / distance / damage specially) ──

/** Group an integer with thousands commas, ICU-free so it's deterministic. */
function grouped(n: number): string {
  const neg = n < 0;
  const s = Math.abs(Math.floor(n)).toString();
  return (neg ? '-' : '') + s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** Ticks (20/s) → "1h 23m 45s" (drops leading zero units). */
export function formatTicks(ticks: number): string {
  const secs = Math.floor(Math.max(0, ticks) / 20);
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/** Centimetres → "12.3 m" or "1.23 km" past a kilometre. */
export function formatDistance(cm: number): string {
  const m = cm / 100;
  return m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${m.toFixed(1)} m`;
}

/** Tenths of a heart → "3.5" (half-heart precision). */
export function formatDamage(tenths: number): string {
  return (tenths / 10).toFixed(1);
}

export interface CustomStatDef {
  readonly id: CustomStat;
  readonly label: string;
  readonly format: (v: number) => string;
}

/** The General tab, in display order. */
export const CUSTOM_STATS: readonly CustomStatDef[] = [
  { id: 'play_time', label: 'Time Played', format: formatTicks },
  { id: 'mob_kills', label: 'Mob Kills', format: grouped },
  { id: 'deaths', label: 'Deaths', format: grouped },
  { id: 'jumps', label: 'Jumps', format: grouped },
  { id: 'distance_walked', label: 'Distance Walked', format: formatDistance },
  { id: 'damage_dealt', label: 'Damage Dealt', format: formatDamage },
  { id: 'damage_taken', label: 'Damage Taken', format: formatDamage },
];

/** One tab per per-id category, in display order. */
export const STAT_CATEGORIES: readonly { readonly id: StatCategory; readonly label: string }[] = [
  { id: 'mined', label: 'Blocks Mined' },
  { id: 'used', label: 'Items Used' },
  { id: 'crafted', label: 'Items Crafted' },
  { id: 'picked_up', label: 'Items Picked Up' },
  { id: 'dropped', label: 'Items Dropped' },
  { id: 'killed', label: 'Mobs Killed' },
  { id: 'broken', label: 'Tools Broken' },
];

// ── Serialized form (persistence, ENG-9) ──
export interface StatsSave {
  readonly custom: readonly (readonly [string, number])[];
  readonly categories: readonly (readonly [string, readonly (readonly [string, number])[]])[];
}

/**
 * Accumulates a player's statistics. `bump` increments a per-id tally; `add`
 * increments a general total. Both return the new value. Zero is never stored, so
 * `entries`/`total` reflect only what actually happened.
 */
export class StatsTracker {
  private readonly cats = new Map<StatCategory, Map<string, number>>();
  private readonly custom = new Map<CustomStat, number>();

  /** Increment a per-id category tally (e.g. bump('mined', 'stone')). No-op for '' id. */
  bump(category: StatCategory, id: string, n = 1): number {
    if (!id || n === 0) return this.get(category, id);
    let m = this.cats.get(category);
    if (!m) this.cats.set(category, (m = new Map()));
    const v = (m.get(id) ?? 0) + n;
    m.set(id, v);
    return v;
  }

  /** Increment a general custom total (e.g. add('mob_kills')). */
  add(stat: CustomStat, n = 1): number {
    const v = this.getCustom(stat) + n;
    this.custom.set(stat, v);
    return v;
  }

  /** A per-id tally's current value (0 if never bumped). */
  get(category: StatCategory, id: string): number {
    return this.cats.get(category)?.get(id) ?? 0;
  }

  /** A general total's current value (0 if never touched). */
  getCustom(stat: CustomStat): number {
    return this.custom.get(stat) ?? 0;
  }

  /** Sum of every tally in a category (the tab header total). */
  total(category: StatCategory): number {
    let sum = 0;
    for (const v of this.cats.get(category)?.values() ?? []) sum += v;
    return sum;
  }

  /** A category's tallies, highest first then id-sorted — the screen's rows. */
  entries(category: StatCategory): [string, number][] {
    return [...(this.cats.get(category) ?? [])].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }

  toJSON(): StatsSave {
    return {
      custom: [...this.custom],
      categories: [...this.cats].map(([cat, m]) => [cat, [...m]] as const),
    };
  }

  load(save: StatsSave | null | undefined): void {
    this.cats.clear();
    this.custom.clear();
    if (!save) return;
    for (const [stat, v] of save.custom) this.custom.set(stat as CustomStat, v);
    for (const [cat, rows] of save.categories) this.cats.set(cat as StatCategory, new Map(rows.map(([k, v]) => [k, v])));
  }
}

/** Ceiling on a single tick's ground movement (blocks) — anything at or above is
 *  a teleport (setblock/tp/portal), not walking, so it never feeds the stat. */
const TELEPORT_STEP = 2;

/** Ground distance travelled this tick, in centimetres (Java's Distance Walked
 *  unit). Zero for a non-move or a teleport-sized jump. The host still gates on
 *  actually being on the ground; this is pure geometry. */
export function distanceCm(dx: number, dz: number): number {
  const d = Math.hypot(dx, dz);
  return d < 1e-4 || d >= TELEPORT_STEP ? 0 : d * 100;
}

/** Whether this tick's ground→air transition is a jump takeoff (left the ground
 *  moving upward) — the Jumps stat counts one per takeoff, not per key press. */
export function isJumpTakeoff(wasOnGround: boolean, onGround: boolean, vy: number): boolean {
  return wasOnGround && !onGround && vy > 0.05;
}
